import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { SvgIconComponent } from '@material-ui/icons';
import { RouteComponentProps } from 'react-router-dom';
import { RoutesEnum } from '../constants';
import {
  ViewGridOutline,
  TicketPercentOutline,
  SubtitlesOutline,
  ShieldSearch,
  AccountHeartOutline,
  FileDocumentOutline,
  Logout
} from 'mdi-material-ui';
import DashboardPage from '../pages/portal-pharmaceutical/dashboard';
import ScheduleIndicationPage from '../pages/portal-pharmaceutical/schedule-indication/pages/ScheduleIndicationPage';
import DiscountsPage from '../pages/portal-pharmaceutical/discounts';
import ContentsPage from '../pages/portal-pharmaceutical/contents';
import RegulamentPage from '../pages/portal-pharmaceutical/regulament';
import MedicineLeafletsPage from '../pages/portal-pharmaceutical/medicine-leaflets/pages/MedicineLeafletsPage';
import { localStorageMethods } from '../pages/portal-pharmaceutical/common/helpers';
import { LocalStorageKeysEnum } from '../pages/portal-pharmaceutical/common/contracts';

type Route = {
  icon?: SvgIconComponent;
  label?: string;
  route: string;
  sidebar?: boolean;
  quickAccess?: boolean;
  dashboard?: boolean;
  exact: boolean;
  component: React.ComponentType<RouteComponentProps<any>> | React.ComponentType<any>;
  onClick?: () => void;
};

export type RoutesContextState = {
  default: string;
  routes: Route[];
};

export type RoutesContextProps = {
  children: ReactNode;
};

const defaultState: RoutesContextState = {
  default: '',
  routes: [],
};

const routes: {
  [key: string]: Route;
} = {
  [RoutesEnum.PHARMACEUTICAL_PANEL_DASHBOARD]: {
    icon: ViewGridOutline,
    label: 'Home portal',
    route: RoutesEnum.PHARMACEUTICAL_PANEL_DASHBOARD,
    sidebar: true,
    dashboard: false,
    component: DashboardPage,
    exact: false,
  },
  [RoutesEnum.CONTENTS_ROUTE]: {
    icon: SubtitlesOutline,
    label: 'Conteúdos',
    route: RoutesEnum.CONTENTS_ROUTE,
    sidebar: true,
    component: ContentsPage,
    exact: false,
  },
  [RoutesEnum.DISCOUNTS_ROUTE]: {
    icon: TicketPercentOutline,
    label: 'Descontos',
    route: RoutesEnum.DISCOUNTS_ROUTE,
    sidebar: true,
    component: DiscountsPage,
    exact: false,
  },
  [RoutesEnum.SCHEDULE_INDICATION_ROUTE]: {
    icon: AccountHeartOutline,
    label: 'Especialistas',
    route: RoutesEnum.SCHEDULE_INDICATION_ROUTE,
    sidebar: true,
    dashboard: false,
    component: ScheduleIndicationPage,
    exact: true,
  },
  [RoutesEnum.MEDICINE_LEAFLETS_ROUTE]: {
    icon: FileDocumentOutline,
    label: 'Bulário de A a Z',
    route: RoutesEnum.MEDICINE_LEAFLETS_ROUTE,
    sidebar: true,
    dashboard: false,
    component: MedicineLeafletsPage,
    exact: true,
  },
  [RoutesEnum.REGULATION_ROUTE]: {
    icon: ShieldSearch,
    label: 'Eventos e Regulamentações',
    route: RoutesEnum.REGULATION_ROUTE,
    sidebar: true,
    component: RegulamentPage,
    exact: true,
  },
  [RoutesEnum.LOGOUT_ROUTE]: {
    icon: Logout,
    label: 'Sair do sistema',
    route: RoutesEnum.LOGOUT_ROUTE,
    sidebar: true,
    component: ContentsPage,
    exact: true,
    onClick: () => {
      localStorageMethods.removeItem(LocalStorageKeysEnum.PHARMACEUTICAL_CREDENTIALS);
      localStorageMethods.removeItem(LocalStorageKeysEnum.PHARMACEUTICAL_CRFUF_LOGIN);
      window.location.href = `${RoutesEnum.PHARMACEUTICAL_LOGIN_PAGE}`;  
    },
  },
};

export const RoutesContext = createContext<RoutesContextState>(defaultState);

export const RoutesProvider = ({ children }: RoutesContextProps): JSX.Element => {
  const [state, setState] = useState<RoutesContextState>(defaultState);

  useEffect(() => {
    const routesArray = Object.values(routes);

    const updatedRoutes = routesArray.map(route => ({
      ...route,
      route: route.route,
    }));

    if (updatedRoutes && updatedRoutes.length) {
      setState({
        default: updatedRoutes[0].route,
        routes: updatedRoutes,
      });
    }
  }, []);

  return <RoutesContext.Provider value={state}>{children}</RoutesContext.Provider>;
};


export const useRoutes = (): RoutesContextState => useContext<RoutesContextState>(RoutesContext);
